/**
 * The one initials helper. Nine near-duplicates existed across `client/src`
 * with four different null policies (none / `if (!name)` / `|| '?'` at the call
 * site / a `string | null` parameter), and the copies with NO policy are what
 * OFFICEHUB-CLIENT-66 is: `UserAvatar` did `user.displayName.split(' ')` on a
 * Firestore-denormalized name that a legacy document did not carry, threw, and
 * took the surrounding page down through the error boundary.
 *
 * The crash vector is ALWAYS the receiver, never the content: `''.split(' ')`
 * returns `['']` and yields `''` harmlessly. So this takes `unknown`-ish input
 * and narrows it, rather than trusting a `string` type that Firestore data has
 * no obligation to honour.
 */

/**
 * Up to two uppercase initials for a person or company name.
 *
 * @param name     a display name that MAY be missing — Firestore denormalized
 *                 fields, `auth.displayName` (null for invite-only accounts),
 *                 and client-supplied public-comment names all can be.
 * @param fallback what to show when there is no usable name. Default `'?'`.
 */
export function getInitials(name?: string | null, fallback = '?'): string {
  if (typeof name !== 'string') return fallback;
  const trimmed = name.trim();
  if (!trimmed) return fallback;
  const initials = trimmed
    .split(/\s+/)
    .map((part) => part[0])
    .filter((char): char is string => Boolean(char))
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return initials || fallback;
}

/**
 * Up to two initials, taken from the FIRST and LAST word.
 *
 * A genuinely different policy from `getInitials`, not a duplicate of it:
 * "Mary Jane Watson" is `MJ` there and `MW` here, and a single word yields two
 * characters (`Cher` -> `CH`) rather than one. Six private copies of exactly
 * this logic existed — AssignmentSelector, LeadAssignmentSelector, LeadList,
 * LeadListMobile, AssetAssigneeSelector and ContactAvatar — none of which
 * guarded the receiver, which is the OFFICEHUB-CLIENT-66 crash shape. The
 * behaviour is preserved verbatim; only the missing null policy is added.
 *
 * @param name     a display name that MAY be missing (Firestore denormalized
 *                 fields, `auth.displayName`, unassigned records).
 * @param fallback what to show when there is no usable name. Default `'?'`.
 */
export function getInitialsFirstLast(name?: string | null, fallback = '?'): string {
  if (typeof name !== 'string') return fallback;
  const trimmed = name.trim();
  if (!trimmed) return fallback;
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return trimmed.slice(0, 2).toUpperCase();
}

/**
 * The single avatar letter for a company or person name.
 *
 * Replaces bare `companyName.charAt(0).toUpperCase()` call sites, where the
 * receiver is a Firestore field with no obligation to exist.
 */
export function firstLetter(name?: string | null, fallback = '?'): string {
  if (typeof name !== 'string') return fallback;
  const trimmed = name.trim();
  if (!trimmed) return fallback;
  return trimmed.charAt(0).toUpperCase();
}
